// SPDX-License-Identifier: MIT
// Dependency injection keeps this adapter usable with an engine's own Three.js instance.
export function createCutaway(THREE, { radius = 1.65, feather = 0.3 } = {}) {
  const center = { value: new THREE.Vector3() };
  const viewport = { value: new THREE.Vector2(1, 1) };
  const size = { value: 0 };
  const strength = { value: 0 };
  const point = new THREE.Vector3();
  const viewPoint = new THREE.Vector3();
  const drawingSize = new THREE.Vector2();
  const patched = new Map();

  function register(root) {
    root.traverse((mesh) => {
      if (!mesh.isMesh) return;
      for (const material of [].concat(mesh.material)) {
        if (!material || material.transparent || patched.has(material)) continue;
        const compile = material.onBeforeCompile;
        const cacheKey = material.customProgramCacheKey;
        const previousKey = cacheKey.call(material);
        const hook = function (shader, renderer) {
          compile.call(this, shader, renderer);
          Object.assign(shader.uniforms, { cutCenter: center, cutViewport: viewport, cutRadius: size, cutStrength: strength });
          shader.fragmentShader = `uniform vec3 cutCenter;
uniform vec2 cutViewport;
uniform float cutRadius;
uniform float cutStrength;
${shader.fragmentShader}`.replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
  vec2 cutDelta = (gl_FragCoord.xy / cutViewport - cutCenter.xy) * vec2(cutViewport.x / cutViewport.y, 1.0);
  float cutMask = 1.0 - smoothstep(cutRadius * ${1 - Math.min(0.95, Math.max(0.01, feather))}, cutRadius, length(cutDelta));
  float cutGrain = fract(52.9829189 * fract(dot(floor(gl_FragCoord.xy / 2.0), vec2(0.06711056, 0.00583715))));
  if (gl_FragCoord.z < cutCenter.z - 0.00001 && cutGrain < cutMask * cutStrength) discard;
`);
        };
        patched.set(material, { compile, cacheKey, hook });
        material.onBeforeCompile = hook;
        material.customProgramCacheKey = () => `${previousKey}:artistic-cutaway-v1:${feather}`;
        material.needsUpdate = true;
      }
    });
  }

  function update(camera, renderer, target, dt, enabled = true) {
    const delta = Number.isFinite(dt) ? Math.max(0, dt) : 0;
    strength.value += ((enabled && target ? 1 : 0) - strength.value) * (1 - Math.exp(-14 * delta));
    if (!target) return;
    camera.updateMatrixWorld();
    point.copy(target);
    viewPoint.copy(point).applyMatrix4(camera.matrixWorldInverse);
    if (viewPoint.z >= -camera.near) { strength.value = 0; return; }
    point.project(camera);
    center.value.set(point.x * 0.5 + 0.5, point.y * 0.5 + 0.5, point.z * 0.5 + 0.5);
    renderer.getDrawingBufferSize(drawingSize);
    viewport.value.copy(drawingSize);
    size.value = Math.min(0.35, Math.max(0.015, radius * camera.projectionMatrix.elements[5] / (-viewPoint.z * 2)));
  }

  function dispose() {
    for (const [material, original] of patched) {
      if (material.onBeforeCompile !== original.hook) continue;
      material.onBeforeCompile = original.compile;
      material.customProgramCacheKey = original.cacheKey;
      material.needsUpdate = true;
    }
    patched.clear();
  }
  return { register, update, dispose };
}
